import React, { useCallback, useEffect, useRef } from 'react'
import classNames from 'classnames'
import { Case, Switch } from 'react-if'
import { GuildScore } from '@/atoms/GuildScore'
import { Image } from '@/atoms/Image'
import { CaptionLG, CaptionMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import {
  FranchiseTabItemInteraction,
  useContentInteraction,
  HOVER_THRESHOLD_MS,
} from '@/services/Analytics/ContentInteractions'
import { Project } from '@/services/ProjectsService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

export interface FranchiseProjectCardProps {
  relatedProject: Project
  itemIndex: number
  pagePlacement: number
  className?: string
  onProjectClick?: (project: Project) => void
}

export const FranchiseProjectCard: React.FC<FranchiseProjectCardProps> = ({
  relatedProject,
  itemIndex,
  pagePlacement,
  className,
  onProjectClick,
}) => {
  const { t } = useTranslate('watch')
  const { trackInteraction } = useContentInteraction()
  const [cardRef, isVisible] = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.9, // Track when at least 90% of the card is visible
  })

  // Hover tracking state
  const hoverStartTimeRef = useRef<number | null>(null)
  const impressionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasTrackedImpression = useRef(false)

  const projectId = relatedProject?.title?.id || relatedProject?.id || ''
  const projectTypeName =
    (relatedProject?.title?.__typename as 'ContentSeries' | 'ContentMovie' | 'ContentSpecial' | null) || null
  const portraitProjectPoster =
    relatedProject?.title?.portraitAngelImage?.cloudinaryPath ??
    relatedProject?.title?.portraitAngelImage3?.cloudinaryPath ??
    relatedProject?.title?.portraitTitleImage?.cloudinaryPath ??
    ''

  const handleMouseEnter = useCallback(() => {
    if (!projectId) return

    // Record the start time
    hoverStartTimeRef.current = Date.now()
  }, [projectId])

  const handleMouseLeave = useCallback(() => {
    if (!projectId || !hoverStartTimeRef.current) return

    const dwellTime = Date.now() - hoverStartTimeRef.current

    // Only track hover if the user hovered for at least the threshold time
    if (dwellTime >= HOVER_THRESHOLD_MS && portraitProjectPoster) {
      const interaction = new FranchiseTabItemInteraction({
        id: projectId,
        __typename: projectTypeName,
        attribution: {
          itemIndex,
          pagePlacement,
          image: {
            cloudinaryPath: portraitProjectPoster,
          },
        },
      })
        .withSurface('brand_landing')
        .withPlacement('franchise_tab', pagePlacement, itemIndex, 'horizontal_swimlane')
        .withImageArtwork(portraitProjectPoster, 'portrait_display')
        .withHover(dwellTime)

      trackInteraction(interaction)
    }

    // Reset state
    hoverStartTimeRef.current = null
  }, [projectId, portraitProjectPoster, projectTypeName, pagePlacement, itemIndex, trackInteraction])

  const handleClick = useCallback(() => {
    if (onProjectClick) {
      onProjectClick(relatedProject)
    }

    if (!projectId || !portraitProjectPoster) return

    const interaction = new FranchiseTabItemInteraction({
      id: projectId,
      __typename: projectTypeName,
      attribution: {
        itemIndex,
        pagePlacement,
        image: {
          cloudinaryPath: portraitProjectPoster,
        },
      },
    })
      .withSurface('brand_landing')
      .withPlacement('franchise_tab', pagePlacement, itemIndex, 'horizontal_swimlane')
      .withImageArtwork(portraitProjectPoster, 'portrait_display')
      .withPress()

    trackInteraction(interaction)
  }, [
    projectId,
    portraitProjectPoster,
    projectTypeName,
    pagePlacement,
    itemIndex,
    trackInteraction,
    onProjectClick,
    relatedProject,
  ])

  // Track impression when the card becomes visible for at least 1 second
  useEffect(() => {
    if (!projectId || hasTrackedImpression.current) return

    if (isVisible) {
      // Start a timer to track impression after 1 second
      impressionTimeoutRef.current = setTimeout(() => {
        hasTrackedImpression.current = true

        if (portraitProjectPoster) {
          const interaction = new FranchiseTabItemInteraction({
            id: projectId,
            __typename: projectTypeName,
            attribution: {
              itemIndex,
              pagePlacement,
              image: {
                cloudinaryPath: portraitProjectPoster,
              },
            },
          })
            .withSurface('brand_landing')
            .withPlacement('franchise_tab', pagePlacement, itemIndex, 'horizontal_swimlane')
            .withImageArtwork(portraitProjectPoster, 'portrait_display')
            .withImpression()

          trackInteraction(interaction)
        }
      }, 1000) // Wait 1 second before tracking impression
    } else {
      // Clear timeout if item becomes not visible before 1 second
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
        impressionTimeoutRef.current = null
      }
    }

    return () => {
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
      }
    }
  }, [isVisible, projectId, portraitProjectPoster, projectTypeName, trackInteraction, itemIndex, pagePlacement])

  return (
    <div
      ref={cardRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-label="suggested-content-card"
      id={relatedProject?.id ?? ''}
      className="relative min-w-[250px] pr-6 lg:min-w-[316px]"
    >
      <div className={classNames('flex flex-col', className)}>
        {portraitProjectPoster && (
          <a
            aria-label={relatedProject?.name ?? ''}
            href={`${paths.watch.index}/${relatedProject?.slug}`}
            className="aspect-h-3 aspect-w-2 relative mb-3 w-full cursor-pointer overflow-hidden rounded-2xl"
            onClick={handleClick}
          >
            <div className="absolute inset-0">
              <Image
                alt={relatedProject?.title?.name ?? ''}
                src={getCloudinaryImageUrl({ path: portraitProjectPoster })}
                width={316}
                height={474}
                className="h-full w-full object-cover"
              />
            </div>
          </a>
        )}
        <div className="flex flex-col gap-y-1">
          <CaptionMD color="core-gray-500">
            <Switch>
              <Case condition={relatedProject?.title?.__typename === 'ContentSeries'}>
                <Translate i18nKey="series" t={t}>
                  Series
                </Translate>
              </Case>
              <Case condition={relatedProject?.title?.__typename === 'ContentMovie'}>
                <Translate i18nKey="movie" t={t}>
                  Movie
                </Translate>
              </Case>
              <Case condition={relatedProject?.title?.__typename === 'ContentSpecial'}>
                <Translate i18nKey="special" t={t}>
                  Special
                </Translate>
              </Case>
            </Switch>
          </CaptionMD>
          <CaptionLG weight="bold">{relatedProject?.name}</CaptionLG>
          {relatedProject?.highestScore ? <GuildScore guildScore={relatedProject?.highestScore} /> : null}
        </div>
      </div>
    </div>
  )
}
